import React, { useState } from "react";
import { Sparkles, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import Card from "./common/Card";
import AffiliateCard from "./common/AffiliateCard";
import {
  AFFILIATE_LINKS,
  ARC_PRODUCTS,
  TOOLS_CATEGORIES,
  FTC_DISCLOSURE,
} from "../config/affiliateLinks";

/* Tab signature accent — NEON magenta from the shared chart tone system
   (--chart-magenta: #E879F9 dark / #C026D3 light). Referenced as a CSS var so
   the accent re-themes with the app, exactly like the glow charts. Used only for
   this tab's highlights (section eyebrows, header mark) — cards, body copy and the
   base AffiliateCard stay on the v4 gold/black tokens. */
const ACCENT = "var(--chart-magenta)";
const ACCENT_SOFT = "color-mix(in srgb, var(--chart-magenta) 15%, transparent)";

/* Magenta signature eyebrow — the tab's one accent voice. */
const Eyebrow = ({ children, className = "" }) => (
  <p
    className={`text-label uppercase ${className}`}
    style={{ color: ACCENT }}
  >
    {children}
  </p>
);

/**
 * ToolsTab — dedicated "Tools & Resources" page.
 *
 * Layout (top to bottom):
 *   1. ARC Premium feature row (Apex + Galaxy) — featured cards, big
 *   2. External affiliate categories — Exchanges, Wallets, DeFi, Security
 *   3. Advanced Trading section — collapsible, gated by user toggle
 *   4. Bonus / Productivity (Wispr Flow at the end per Gven's call)
 *   5. FTC affiliate disclosure footer
 */
const ToolsTab = () => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const advancedLinks = AFFILIATE_LINKS.filter(
    (l) => !l.skip && l.category === "advanced"
  );
  const bonusLinks = AFFILIATE_LINKS.filter(
    (l) => !l.skip && l.category === "bonus"
  );

  return (
    <div className="space-y-6">
      {/* ─── Page header ────────────────────────────────────────────── */}
      <Card hover={false}>
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-arc-sm"
            style={{ background: ACCENT_SOFT }}
          >
            <Sparkles size={15} style={{ color: ACCENT }} />
          </span>
          <Eyebrow>Tools &amp; Resources</Eyebrow>
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-arc-ink mb-2">
          Swami's curated stack
        </h2>
        <p className="text-arc-ink-2 text-sm leading-relaxed max-w-2xl">
          The exchanges, wallets, security tools, and education programs Swami
          actually uses and recommends. Each link is tracked so we can see what
          actually helps you — and a few of them earn ARCrypto a small commission
          (see disclosure below).
        </p>
      </Card>

      {/* ─── ARC Premium products — featured row ───────────────────── */}
      <Card hover={false}>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <Eyebrow className="mb-1">Work directly with ARC</Eyebrow>
            <h3 className="text-h2 text-arc-ink">Premium programs</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AffiliateCard
            link={ARC_PRODUCTS.apexone}
            medium="tools-tab-apex-featured"
            featured
          />
          <AffiliateCard
            link={ARC_PRODUCTS.galaxy}
            medium="tools-tab-galaxy-featured"
            featured
          />
        </div>
      </Card>

      {/* ─── External affiliate categories ─────────────────────────── */}
      {TOOLS_CATEGORIES.map((cat) => {
        const links = AFFILIATE_LINKS.filter(
          (l) => !l.skip && l.category === cat.key
        );
        if (links.length === 0) return null;
        return (
          <Card key={cat.key} hover={false}>
            <div className="mb-4">
              <Eyebrow className="mb-1">{cat.label}</Eyebrow>
              <p className="text-sm text-arc-ink-3">{cat.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {links.map((l) => (
                <AffiliateCard
                  key={l.id}
                  link={l}
                  medium={`tools-tab-${cat.key}`}
                />
              ))}
            </div>
          </Card>
        );
      })}

      {/* ─── Advanced Trading — collapsible, gated section ──────────── */}
      {advancedLinks.length > 0 && (
        <Card hover={false}>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="btn-tactile w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-arc-sm bg-arc-warn-soft ring-1 ring-arc-line flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={16} className="text-arc-warn" />
              </div>
              <div>
                <p className="text-label uppercase text-arc-warn mb-1">
                  Advanced Trading
                </p>
                <p className="text-sm text-arc-ink-3">
                  Leveraged perps platforms — high risk, experienced traders only
                </p>
              </div>
            </div>
            {showAdvanced ? (
              <ChevronUp size={18} className="text-arc-ink-4 flex-shrink-0" />
            ) : (
              <ChevronDown size={18} className="text-arc-ink-4 flex-shrink-0" />
            )}
          </button>

          {showAdvanced && (
            <div className="mt-4 pt-4 border-t border-arc-line">
              <div className="flex gap-2.5 rounded-arc-ctl ring-1 ring-arc-line bg-arc-warn-soft p-3 mb-4">
                <AlertTriangle
                  size={15}
                  className="text-arc-warn flex-shrink-0 mt-0.5"
                />
                <p className="text-xs text-arc-ink-2 leading-relaxed">
                  <span className="font-semibold text-arc-warn">
                    Leverage warning:
                  </span>{" "}
                  these platforms let you trade with borrowed money. You can lose
                  more than you put in. Only use these if you understand
                  liquidation risk, position sizing, and have already built a
                  stable base of spot crypto holdings.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {advancedLinks.map((l) => (
                  <AffiliateCard
                    key={l.id}
                    link={l}
                    medium="tools-tab-advanced"
                  />
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ─── Bonus / Productivity (Wispr Flow at the end) ───────────── */}
      {bonusLinks.length > 0 && (
        <Card hover={false}>
          <div className="mb-4">
            <Eyebrow className="mb-1">Productivity bonus</Eyebrow>
            <p className="text-sm text-arc-ink-3">
              Not finance — just useful tools from Swami's stack
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bonusLinks.map((l) => (
              <AffiliateCard
                key={l.id}
                link={l}
                medium="tools-tab-bonus"
              />
            ))}
          </div>
        </Card>
      )}

      {/* ─── FTC Disclosure ────────────────────────────────────────── */}
      <div className="rounded-arc-ctl ring-1 ring-arc-line bg-arc-card p-4">
        <p className="text-[11px] text-arc-ink-4 leading-relaxed">
          <span className="font-semibold text-arc-ink-3">Disclosure: </span>
          {FTC_DISCLOSURE}
        </p>
      </div>
    </div>
  );
};

export default ToolsTab;
